import {BaseTimelineView} from "./baseTimelineView";
import {TimelineEntries} from "../timelineTypes";
import {getISODate, Temporal} from "@/shared/utils/datefunctions";

/**
 * Web component for displaying a daily view of the timeline.
 * The days from the current start to the current end are retrieved and displayed vertically. (Currently, this component is only used to display a single day.)
 * If the time function is available, <code>TimelineDay</code> is generated and displayed for each day.
 * Otherwise - if the project time feature is available - <code>ProjectTimeTimelineDay</code> is generated to display the timeline for project time only.
 *
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class TimelineDaysView extends BaseTimelineView {
	private _currentStart: Temporal.PlainDate = Temporal.Now.plainDateISO();
	private _currentEnd: Temporal.PlainDate;

	/**
	 * Loads the timeline with the specified parameters<p>
	 * Therefore the properties setters are called, the generate timeline request, reset the timeline days array and update the range info
	 */
	public load(userId: number, date: Temporal.PlainDate): void {
		this.userId = userId;
		this.currentStart = date;
		this.currentEnd = date;

		this.generateTimeline(this.currentStart, this.currentEnd, true)
			.done((timelineEntries: TimelineEntries) => {
				const {workingTimeTimelineDays, projectTimeTimelineDays, combinedRecordingStatus} = timelineEntries;

				this.populateTimeline(
					workingTimeTimelineDays,
					projectTimeTimelineDays,
					combinedRecordingStatus,
					true
				);
			});
	}

	/**
	 * Reloads the timeline<p>
	 * This is done by the generate timeline request, reset the timeline days array and scroll to the original position
	 */
	public reload(): void {
		// save current scroll position
		const positionBeforeReload = window.scrollY;

		this.generateTimeline(this.currentStart, this.currentEnd, true)
			.done((timelineEntries: TimelineEntries) => {
				const {workingTimeTimelineDays, projectTimeTimelineDays, combinedRecordingStatus} = timelineEntries;

				this.populateTimeline(
					workingTimeTimelineDays,
					projectTimeTimelineDays,
					combinedRecordingStatus,
					false
				);

				window.scrollTo({left: 0, top: positionBeforeReload, behavior: "auto"});
			});
	}

	private generateTimeline(startDate: Temporal.PlainDate, endDate: Temporal.PlainDate, showLoadingSkeleton = false): JQueryXHR {
		return this.requestTimelineEntries(
			"timeline/generateDailyTimelineEntries.json",
			{
				end: getISODate(endDate),
				start: getISODate(startDate)
			},
			showLoadingSkeleton
		);
	}

	get currentStart(): Temporal.PlainDate {
		return this._currentStart;
	}

	set currentStart(value: Temporal.PlainDate) {
		this._currentStart = value;
	}

	get currentEnd(): Temporal.PlainDate {
		return this._currentEnd;
	}

	set currentEnd(value: Temporal.PlainDate) {
		this._currentEnd = value;
	}
}
